/**
 * **页面编辑器的模型层** —— 把「文档通道里的节点」变成「纸上能画的元素」，
 * 以及所有和 mm 几何有关的纯函数。
 *
 * ## 为什么这一层是纯函数
 *
 * 它上面（组件）只做两件事：把状态摆成 DOM、把指针事件换算成 mm。
 * 所有"算"的东西都在这里 —— 于是**不需要挂载 Vue、不需要 DOM** 就能断言：
 * 命中判定、吸附、夹取、缩放、标尺步长。
 * 这个仓库里踩过的坑（吸附吸到纸上外面、resize 把框翻面、命中判到下面的元素）
 * 全是这一层的事，而它们**在 DOM 里查起来最费时间**。
 *
 * ## 数据从哪来：节点是唯一真源
 *
 * ```text
 *   print.page 节点 ──items.0 / items.1 …（边）──► print.component.* 节点
 *        │                                              │
 *        │ 尺寸 / dpi / 边距（参数）            几何（参数）、内容（求值结果优先）
 *        ▼                                              ▼
 *                  PageView / ElementView（渲染用的视图模型）
 * ```
 *
 * ⚠️ **成员关系读边、几何读参数**：这是 D2 那句话的落点 ——
 * 「元素节点的几何是**参数**，不是连线」。所以这里从来不问
 * "谁在谁右边"，只问"它接在哪个页面上"和"它的 x/y 是多少"。
 *
 * ⚠️ **内容优先读求值结果**（`outputs`）：文本组件的 `text` 行常常接了
 * 数据源，那时内联控件里那个值是**默认值**、不是真值。
 * 位置 / 尺寸反过来**只读参数** —— 那是设计态的真源，而且求值失败时
 * 也必须在（否则"包没装好"会让整个版面消失，而不是只丢内容）。
 */
import type { EditorLinkView, EditorNodeView } from '@/types/editor'
import {
  clamp,
  DEFAULT_BACKGROUND,
  DEFAULT_DPI,
  DEFAULT_PAPER,
  MM_PER_INCH,
  mmToDots,
  normalizeRotation,
  roundMm,
} from './units'
import {
  BARCODE_PLACEHOLDER_MM,
  encode,
  hriHeightMm,
  moduleDotsFor,
  symbologyOf,
  xDimWarning,
  type EncodedSymbol,
  SYMBOLOGIES,
} from './barcode'
import {
  BARCODE_NODE_TYPE,
  BOX_NODE_TYPE,
  DOCUMENT_NODE_TYPE,
  IMAGE_NODE_TYPE,
  PAGE_NODE_TYPE,
  QRCODE_NODE_TYPE,
  TEXT_NODE_TYPE,
} from './nodes'

/* ══════════════════════════════════════════════════════════════════
   节点类型判别
   ══════════════════════════════════════════════════════════════════ */

/*
 * ⚠️ 类型标识**从 `nodes.ts` 引**，不在这里再写一遍字符串：
 * 两处各写一份的表现是"改了节点类型，页面编辑器认不出来"（东西还在，
 * 只是画不出来），而那要打开界面才发现。
 */
export const PAGE_TYPE = PAGE_NODE_TYPE
export const TEXT_TYPE = TEXT_NODE_TYPE
export const BOX_TYPE = BOX_NODE_TYPE
export const BARCODE_TYPE = BARCODE_NODE_TYPE
export const QRCODE_TYPE = QRCODE_NODE_TYPE
export const IMAGE_TYPE = IMAGE_NODE_TYPE
export const DOCUMENT_TYPE = DOCUMENT_NODE_TYPE
/** 所有组件节点类型（页面编辑器认得的那几种） */
export const COMPONENT_TYPES: readonly string[] = [TEXT_TYPE, BOX_TYPE, BARCODE_TYPE, QRCODE_TYPE, IMAGE_TYPE]

export function isPageType(type: string): boolean {
  return type === PAGE_TYPE
}

export function isComponentType(type: string): boolean {
  return COMPONENT_TYPES.includes(type)
}

/* ══════════════════════════════════════════════════════════════════
   视图模型
   ══════════════════════════════════════════════════════════════════ */

export interface ElementBox {
  xMm: number
  yMm: number
  wMm: number
  hMm: number
}

/**
 * **条码 / 二维码**那点额外信息（只有这一种组件有）。
 *
 * ⚠️ 它是"布局期算出来的那一份"：模块宽吸附成整数点、宽度由模块数推出。
 * 面板要显示这些数字（用户填的是 X 维度 µm，看到的必须是**取整之后**的真实值）。
 */
export interface BarcodeView {
  /** 2D（正方形）：拖任意一边都是改模块宽，没有独立的条高 */
  square: boolean
  symbology: string
  label: string
  text: string
  xDimMicron: number
  showHri: boolean
  /** 目标宽度（mm）—— 0 = 自动（按 `xDimMicron` 算）；>0 时模块宽由它反推 */
  targetWidthMm: number
  /** 目标总高（mm，**含 HRI**）—— 0 = 自动 */
  targetHeightMm: number
  /** 一个模块几个设备点（整数，`units.ts` 换算） */
  moduleDots: number
  /** 模块宽是**被目标宽度定下来的**吗（面板要显示"按宽度反推"） */
  moduleFromWidth: boolean
  /** 一个模块多少 mm（取整之后的真实值） */
  moduleMm: number
  /**
   * ★★ **框让出来的内缩量**（mm，单侧）—— 有边框时才是 0 以上的值。
   *
   * 语义：**框画在纸上，静区在框里面，两者不许重叠**。框（`lineWidthMm`）画在
   * **组件框**上，而组件框是"含静区的占地" —— 不内缩的话线宽的那一半就压在
   * 静区里（`verify:raster` 那条"静区里一个墨点都没有"正是它抓出来的）。
   *
   * 所以有框时：内容（条 / 模块 + 两侧静区 + HRI）整体内缩 `insetMm`，
   * 而**组件框跟着长大 `2 × insetMm`**（框要把让出来的那一圈算进自己的占地）——
   * 用户设的线宽是真画出来的，静区也还是干净的。
   *
   * ⚠️ 取整按**设备点**（`ceil(线宽点数 / 2)`）：描边以框线为中心，内侧那半个
   * 线宽才是"压进内容区"的那一半。在 mm 上留半个小数，203dpi 下会变成
   * "有时候 0 点、有时候 1 点"的不确定行为。
   */
  insetMm: number
  /** 框太粗 / 目标宽度太窄、内容放不下时的警告（人能读的那句话）；正常时 null */
  frameWarning: string | null
  /** 内容区宽度（mm，不含静区） */
  contentWidthMm: number
  /** 静区宽度（mm，单侧） */
  quietZoneMm: number
  /** 条高（mm） */
  barHeightMm: number
  /** HRI 那一行的高度（mm）—— 没开 HRI 时是 0 */
  hriMm: number
  /** HRI 字号参数（mm）；0 = 自动（见 `hriHeightMm`） */

  /** 二维码纠错等级（一维是 `''`） */
  ecLevel: string
  /**
   * **前景色**（条 / 模块的颜色，`#rrggbb`）。
   *
   * ⚠️ 复用元素上的 `color`（文本那边叫"文字颜色"）：它们本来就是同一件事
   * —— **墨的颜色**。真机反馈："条码的和二维码的颜色，应该可以复用背景色，
   * 然后增加一个前景色，默认前景色应该是黑的，背景色是白色透明的"。
   */
  color: string
  /** 编码结果（模块几何）；`null` = 编码失败 */
  encoded: EncodedSymbol | null
  /** 编码失败的原因（人能读的那句话）；成功时 null */
  error: string | null
  /** X 维度合规警告（GS1 下限）；合格时 null */
  xDimWarning: string | null
}

/**
 * 文本的垂直对齐（框比字高时贴在框的哪一边）
 *
 * ⚠️ 和 `align`（水平）分开两个参数、而不是合成一个九宫格：
 * 标签上"左对齐 + 垂直居中"这种组合很常见，而九宫格枚举会让
 * "只想改水平"变成一次九选一。
 */
export type TextVAlign = 'top' | 'middle' | 'bottom'

/**
 * 图片组件的视图（源 + 缩放方式 + 阈值）。
 *
 * ⚠️ `src` 是**地址**（URL / data: URI）：资产库还没做，所以文档里存的是
 * 地址而不是内嵌数据 —— 这会随"资产"那一轮一起收口（架构文档 §6.8）。
 */
export interface ImageView {
  src: string
  fit: 'contain' | 'cover' | 'fill'
  threshold: number
  /** 有没有可画的源（空源 → 编辑器显示占位框、IR 给一条 warning） */
  hasSource: boolean
}

export interface ElementView {
  id: string
  nodeType: string
  title: string
  kind: 'text' | 'box' | 'barcode' | 'image'
  box: ElementBox
  /** 文本组件的内容（求值结果优先 —— 见文件头） */
  text: string
  sizeMm: number
  align: 'left' | 'center' | 'right'
  vAlign: TextVAlign
  /** 加粗（浏览器 / Skia 各自用宿主字体渲染，IR 里是一个字段） */
  bold: boolean
  /**
   * **墨色**（`#rrggbb`）：文本 = 字色，条码 / 二维码 = 前景色（条与模块）。
   *
   * ⚠️ 名字是 `color`（参数表里那一行也叫「颜色」），但语义是**墨的颜色**：
   * 单色热敏非白即墨，多色带机器按它分版。所以条码元素这一项**不许写死**
   * —— 它就是 `BarcodeView.color` 的同一个值（见 `barcodeElementOf` 里的
   * `inkColor`：两处各读一次参数就会分叉成"编辑视图不跟手、预览跟手"）。
   */
  color: string
  /**
   * **不透明度**（0–1）。
   *
   * ⚠️ 热敏是**二值**的：半透明只能靠**抖动**近似，而抖不抖是执行侧的事
   * （有些机型 / 指令集根本不支持）。所以它在编辑与预览里是真的，
   * 进了 IR 之后由执行侧决定 —— 不支持时**忽略**（而不是报错）。
   */
  opacity: number
  /** 行距（mm）；0 = 自动（字号的 1.15 倍） */
  lineHeightMm: number
  /** 边框颜色（`#rrggbb`）。热敏是单色，这个值在预览/多色机上有意义 */
  strokeColor: string
  /** 背景色（`fill` 打开时才画） */
  fillColor: string
  lineWidthMm: number
  fill: boolean
  /** 圆角半径（mm）；0 = 直角 */
  radiusMm: number
  /**
   * 反白：黑底白字。
   *
   * ⚠️ 热敏打印机**打不出白墨** —— 这一项的实现是"先铺一块实心黑、
   * 再把字掏空"，所以它只在**矢量输出**（ZPL 的 `^FR` / 光栅反相）里成立；
   * 光栅化那一侧要按"先画底再按反相写"来理解（契约写在 type-contract §8.1）。
   */
  invert: boolean
  /** 旋转（度，顺时针，**绕框中心**）—— 0 / 90 / 180 / 270 */
  rotationDeg: number
  /** 条码 / 二维码专用（别的组件是 null） */
  barcode: BarcodeView | null
  /** 图片专用（别的组件是 null） */
  image: ImageView | null
}

export interface PageView {
  /**
   * **页面节点的 id** —— 写参数 / 选中 / 删节点都用它（节点层的东西）。
   *
   * ⚠️ 它**不是**这张纸的唯一标识：同一个页面节点接两个 `pages.N` 时，
   * 两页的 `id` 相同（同一个节点）而 `key` 不同。要认"哪一张纸"就用 `key`。
   */
  id: string
  /**
   * ★ **这一张纸的实例标识**：`${节点id}#${槽位}`。
   *
   * ⚠️ 为什么不能拿 `id` 当纸的标识（真机反馈："我把同一个页面链接到文档两次，
   * 没有产生两个页面"）：同一页印两次是**正当**的用法，那时两张纸的 `id` 一模一样，
   * 而 DOM 的 `data-npp-page` / 指针换算 / 选中都要能分清"我在哪一张纸上拖"。
   */
  key: string
  /** 文档里的**槽位号**（`pages.N` 的 N）；单页模式 = 页面在视图里的序号 */
  index: number
  title: string
  widthMm: number
  heightMm: number
  dpi: number
  marginMm: number
  /**
   * **纸的底色**（`#rrggbb`）—— 从文档解析（`resolvedPaperOf`）。
   *
   * ⚠️ 白不是"随便一个颜色"：它是**输出语义**（不铺底 = 热敏纸本色）；
   * 别的颜色意味着整张铺满。编辑器 / 预览 / IR 三处读的是同一个值。
   */
  backgroundColor: string
  elements: ElementView[]
}

/** 编辑器读文档需要的三样东西（`doc` 就是它） */
export interface PrintDocView {
  readonly nodes: readonly EditorNodeView[]
  readonly links: readonly EditorLinkView[]
  readonly outputs: ReadonlyMap<string, ReadonlyMap<string, unknown>>
}

/* ══════════════════════════════════════════════════════════════════
   读参数
   ══════════════════════════════════════════════════════════════════ */

/**
 * 读一个颜色值：只接受 `#rgb` / `#rrggbb`（别的一律回退默认）。
 *
 * ⚠️ 不做"任意 CSS 颜色名"的宽容：这个值会进 IR 交给**输出侧**，
 * 而输出侧不该带一个 CSS 解析器。规范成一种写法，两端都好办。
 */
/**
 * 二维码节点没有 `symbology` 时的兜底符号。
 *
 * ⚠️ 从**符号表**里取第一个二维符号（不写死 `'qrcode'` 字符串）：
 * 哪天表里换了主推的二维码，这里自动跟上。
 */
const DEFAULT_2D_SYMBOLOGY = SYMBOLOGIES.find((item) => item.kind === '2d')?.id ?? 'qrcode'

/** 不透明度夹到 0–1（文档可能来自别处；越界值会让渲染侧行为不可预期） */
function clamp01(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 1
}

function colorOf(raw: unknown, fallback: string): string {
  const value = typeof raw === 'string' ? raw.trim() : ''
  return /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value) ? value : fallback
}

function numOf(values: Readonly<Record<string, unknown>>, id: string, fallback: number): number {
  const raw = values[id]
  const asNumber = typeof raw === 'number' ? raw : Number(raw)
  return Number.isFinite(asNumber) ? asNumber : fallback
}

function textOf(values: Readonly<Record<string, unknown>>, id: string, fallback: string): string {
  const raw = values[id]
  return raw === undefined || raw === null ? fallback : String(raw)
}

/** 一个节点上的几何（mm）—— 缺值给默认值，理由见 `evaluators.ts` 的取舍 ② */
export function boxOf(node: EditorNodeView, fallback: ElementBox = { xMm: 0, yMm: 0, wMm: 20, hMm: 10 }): ElementBox {
  return {
    xMm: numOf(node.values, 'xMm', fallback.xMm),
    yMm: numOf(node.values, 'yMm', fallback.yMm),
    wMm: numOf(node.values, 'wMm', fallback.wMm),
    hMm: numOf(node.values, 'hMm', fallback.hMm),
  }
}

/** 求值出来的组件值（没有 / 形状不对 → null） */
function evaluatedComponent(
  doc: PrintDocView,
  nodeId: string,
): {
  text?: unknown
  sizeMm?: unknown
  align?: unknown
  vAlign?: unknown
  bold?: unknown
  color?: unknown
  opacity?: unknown
  lineHeightMm?: unknown
  lineWidthMm?: unknown
  fill?: unknown
  strokeColor?: unknown
  fillColor?: unknown
  src?: unknown
  fit?: unknown
  threshold?: unknown
  radiusMm?: unknown
  invert?: unknown
  rotationDeg?: unknown
  symbology?: unknown
  xDimMicron?: unknown
  showHri?: unknown

} | null {
  const value = doc.outputs.get(nodeId)?.get('out')
  if (!value || typeof value !== 'object') return null
  const props = (value as { props?: unknown }).props
  return props && typeof props === 'object' ? (props as Record<string, unknown>) : null
}

/**
 * **条码元素**：几何要算，不能只读参数（宽度是编码结果推出来的）。
 *
 * 三层数据各司其职：
 *   · **求值结果**（`outputs`）里的 `encoded` —— 权威编码（真源）；
 *   · 没有它（求值失败 / 还没算）→ **在这里现编一次**：同一个纯函数，
 *     结果一样，而且能拿到**具体错在哪**（求值失败时编辑器只剩一片空白
 *     是最糟的：用户不知道是"没内容"还是"编码错了"）；
 *   · 宽度 = 模块数 × 整数点模块宽（`dpi` 从**页面**来）。
 */
function barcodeElementOf(
  doc: PrintDocView,
  node: EditorNodeView,
  dpi: number,
): ElementView {
  const evaluated = evaluatedComponent(doc, node.id)
  /*
   * ⚠️ 符号的兜底**按节点类型分**：二维码节点没有 `symbology` 时（老文档、
   * 手工构造的视图、夹具）**不能退回 `code128`** —— 那会把它画成一维条码
   * （`encoded.kind === '1d'`），而用户要的是一张二维码。
   * 这类"缺省值把类型搞错"的错在界面上很难看出来（都是"一坨条"）。
   */
  const fallbackSymbology = node.type === QRCODE_TYPE ? DEFAULT_2D_SYMBOLOGY : 'code128'
  const symbology = String(evaluated?.symbology ?? textOf(node.values, 'symbology', fallbackSymbology))
  const spec = symbologyOf(symbology)
  const text = String(evaluated?.text ?? textOf(node.values, 'text', ''))
  const xDimMicron = Number(evaluated?.xDimMicron ?? numOf(node.values, 'xDimMicron', 375))
  const square = spec?.kind === '2d' || node.type === QRCODE_TYPE
  const showHri = !square && Boolean(evaluated?.showHri ?? node.values.showHri ?? true)
  /*
   * HRI 的字号就是「字体」里的字号（`sizeMm`，0 = 自动）。
   * ⚠️ 它必须在这里读出来 —— 条码元素与文本元素共用同一份字号语义。
   */
  const sizeMm = Math.max(0, Number(evaluated?.sizeMm ?? numOf(node.values, 'sizeMm', 0)))

  const ecLevel = square ? textOf(node.values, 'ecLevel', 'M') : ''

  /* 权威结果优先（求值已经编过一次），没有才现编 */
  const evaluatedSymbol = (evaluatedEncodeOf(doc, node.id) ?? null)
  const attempt = evaluatedSymbol ? { ok: true as const, symbol: evaluatedSymbol } : encode(symbology, text)
  const encoded = attempt.ok ? attempt.symbol : null
  const error = attempt.ok ? null : attempt.message
  const quietModules = encoded?.quietZone ?? spec?.quietZone ?? 0

  /*
   * ══ ★★ 外观那一组（线宽 / 边框色 / 背景色 / 填充 / 圆角）也要读参数 ══
   *
   * 真机反馈："预览模式的组件边框是默认值，不是设置值，然后就编辑模式显示粗，
   * 预览就细了"。根因就在这里：这几个字段原来**写死**（线宽 0 / 黑框 / 白底 /
   * 不填充 / 直角），而条码 / 二维码的参数表里它们**是有行的**
   * （条码继承自矩形那条链，见 `params.ts` 的 `COMPONENT_BASE`）——
   * 于是那几行是"改了没反应的控件"：
   *
   * ```text
   *   ElementView.lineWidthMm = 0  → 编辑器不画框层
   *   IR 的 pushFrameOp() 看到 0   → 不发 frame op（预览里没有那条 rect）
   * ```
   *
   * ⚠️ 它们必须在**几何之前**读出来：框会让内容内缩（见下面 `insetMm`）。
   *
   * ⚠️ 兜底那一份必须和 `elementOf()`（文本 / 矩形）**逐字一致**：
   * 两处各写一个默认值，就是"同一个参数在两个组件上默认不同"那类漂移。
   * ⚠️ 例外只有**线宽**：缺值时给 **0**（不画框），理由见下面那条。
   */
  const strokeColor = colorOf(evaluated?.strokeColor ?? node.values.strokeColor, '#000000')
  const fillColor = colorOf(evaluated?.fillColor ?? node.values.fillColor, '#ffffff')
  /*
   * ⚠️★ 线宽的缺值兜底是 **0**（不是参数表里的 0.3），而且这是**故意的**：
   *
   *   · 正常路径上节点**存着**那个 0.3mm（控件默认值），所以"用户设的值一定生效"；
   *   · 只有"值根本不存在"的视图（手工构造的夹具 / 迁移前的文档）才走这一支，
   *     而这时**凭空画一条 0.3mm 的边是要出事的**：条码的框 = 组件框，而组件框
   *     **含静区** —— 那条边会直接吃掉左右静区里的两三个点（`verify:raster`
   *     的"静区里一个墨点都没有"那条断言抓的就是这件事）。
   *   · `imageElementOf()` 早就是 0（见那边的 `numOf(..., 'lineWidthMm', 0)`）。
   * 所以：**有值就照值画，没值就不画** —— 宁可少一条框，不要凭空多一条。
   */
  const lineWidthMm = Math.max(0, Number(evaluated?.lineWidthMm ?? numOf(node.values, 'lineWidthMm', 0)))
  const fill = Boolean(evaluated?.fill ?? node.values.fill ?? false)
  const radiusMm = Math.max(0, Number(evaluated?.radiusMm ?? numOf(node.values, 'radiusMm', 0)))

  /*
   * ══ ★★ **框不许吃掉静区**：有框时内容整体内缩半个线宽 ══
   *
   * 框画在**组件框**上（`pushFrameOp` 那条 rect = `element.box`），而条码的
   * 组件框是"**含静区的占地**" —— 不内缩的话，描边内侧的那半个线宽就压在静区里，
   * 而静区是扫码率的前提（`verify:raster` 的"静区里一个墨点都没有"）。
   *
   * 所以规矩是：**框画在纸上，静区在框里面，两者不重叠**。
   *   · `insetDots = 线宽的整点数`（理由见下）；
   *   · 内容（两侧静区 + 条 / 模块 + HRI）整体内缩 `insetMm`；
   *   · 组件框因此**长大 `2 × insetMm`** —— 让出来的那一圈算进框的占地。
   *
   * ⚠️★ **内缩取整个线宽，不是半个** —— 三个消费者对"描边画在哪"的约定不同：
   *
   * ```text
   *   光栅器（raster.ts）   线的墨全在框**内**（dx < lineWidthDots）→ 内边缘 = +整条线宽
   *   CSS 边框（编辑器）     border-box，同理            → 内边缘 = +整条线宽
   *   SVG stroke（预览）    以框线为中心                 → 内边缘 = +半个线宽
   * ```
   *
   * 按"半个"内缩的话，光栅器那一侧会**多压进静区 1 个点**（`verify:raster` /
   * 这一节的"静区里一个墨点都没有"当场红）。按"整个"内缩，则三者都不越界 ——
   * 代价只是矢量预览里框与静区之间可能留半个线宽的空隙。**宁可留缝，不许压静区**。
   *
   * ⚠️ 取整在**设备点**上做（不在 mm 上留半个小数）：203dpi 下 0.3mm 是 2.4 点，
   * 落到 mm 上会变成"有时候 2 点、有时候 3 点"的不确定行为。
   */
  const frameDots = lineWidthMm > 0 ? Math.max(1, mmToDots(lineWidthMm, dpi)) : 0
  const insetDots = frameDots
  const insetMm = (insetDots * MM_PER_INCH) / dpi

  /*
   * ══ 尺寸：**宽 / 高是文档里的参数**，模块宽是算出来的 ══
   *
   * ⚠️ 这一套是照真机反馈改的：以前"宽"是「模块宽 × 模块数」的结果，
   * 于是换个符号（模块数变了）或者改个内容，画出来的东西**会超出框** ——
   * 因为框是旧的宽度、内容是新的模块数。
   *
   * 现在：
   *   · 用户写的 `wMm` 是**目标宽度**（0 = 自动，退回"按 X 维度算"）；
   *   · 模块宽 = （目标宽度 − 两侧内缩）÷ 模块数，**向下取整到整数点**
   *     （`floor` 而不是 round：宁可差一点点也不要画出去），至少 1 个点；
   *   · 于是"画出来的宽度 ≤ 框宽"是**结构上成立**的，不靠调用方自觉。
   *
   * ⚠️ 条码的 `hMm` **含下面那行 HRI**（用户："文本应该在整个高度内部来算"）：
   * 条高 = hMm − HRI 高，而不是"条高 + HRI 高 = 总高"。
   */
  const fallbackWidthMm = encoded ? encoded.modules * ((moduleDotsFor(xDimMicron, dpi) * MM_PER_INCH) / dpi) : BARCODE_PLACEHOLDER_MM
  const rawWidth = numOf(node.values, 'wMm', 0)
  const rawHeight = numOf(node.values, 'hMm', 0)
  /* 2D 是正方形：宽高写哪个都行（两个都写以宽为准） */
  const targetWidthMm = square ? (rawWidth > 0 ? rawWidth : rawHeight) : rawWidth
  /*
   * ⚠️ HRI 的**字号复用「字体」里的字号**（`sizeMm`），不再单开一个参数
   * —— 真机反馈："条码的可读文本，我觉得只保留显示就行了，字号可以复用字体里边的字号"。
   * 高度按字号推（`hriHeightMm` 只认"显式 mm 或按比例推"两种口径）。
   */
  const hriAutoMm = showHri ? hriHeightMm(12, sizeMm > 0 ? sizeMm : 0) : 0
  const targetHeightMm = square ? targetWidthMm : rawHeight
  /* 目标宽度里**先扣掉框让出来的两侧**，剩下的才是内容（含静区）的可用宽度 */
  const innerWidthMm = Math.max(0, targetWidthMm - insetMm * 2)

  const moduleDots = (() => {
    if (!encoded || targetWidthMm <= 0) return moduleDotsFor(xDimMicron, dpi)
    const raw = innerWidthMm / encoded.modules / (MM_PER_INCH / dpi)
    return Math.max(1, Math.floor(raw))
  })()
  const moduleMm = (moduleDots * MM_PER_INCH) / dpi
  const moduleFromWidth = Boolean(encoded) && targetWidthMm > 0

  const contentMm = encoded ? (encoded.kind === '1d' ? encoded.contentModules : encoded.cols) * moduleMm : 0
  const quietZoneMm = quietModules * moduleMm
  /* 条高 = 总高 − 两侧内缩 − HRI（HRI 在总高**内部**）—— 高没写就给 12mm 条 + HRI */
  const widthMm = encoded ? encoded.modules * moduleMm : BARCODE_PLACEHOLDER_MM
  const barHeightMm = square
    ? 0
    : targetHeightMm > 0
      ? Math.max(MIN_BARCODE_HEIGHT_MM, targetHeightMm - insetMm * 2 - (showHri ? hriAutoMm : 0))
      : 12
  const hriMm = showHri ? hriHeightMm(barHeightMm, sizeMm > 0 ? sizeMm : 0) : 0
  /*
   * 高：1D = **两侧内缩 + 条高 + HRI**；2D = 边长 + 两侧内缩（正方形，含静区）。
   * ⚠️ 内缩要算进**组件框**（框要把让出来的那一圈画在自己边上），
   * 而"内容在框里面"这件事因此是结构上成立的。
   */
  const boxWidthMm = widthMm + insetMm * 2
  const heightMm = square ? boxWidthMm : barHeightMm + hriMm + insetMm * 2
  const box = {
    xMm: numOf(node.values, 'xMm', 0),
    yMm: numOf(node.values, 'yMm', 0),
    wMm: roundMm(boxWidthMm),
    hMm: roundMm(heightMm),
  }
  /*
   * ★ **框太粗 / 目标宽度太窄** → 一条能读的警告（而不是画出一个压着静区的条码）。
   *
   * 判据：内容（含静区）+ 框让出来的那一圈 **超过了用户给的目标宽度** ——
   * 这时模块宽已经被 `floor` 压到最小 1 个点，再挤也挤不进去了。
   * 正常路径（目标宽度够）永远不触发：`floor` 保证"占地 ≤ 目标"。
   */
  const frameWarning =
    frameDots > 0 && targetWidthMm > 0 && widthMm + insetMm * 2 > targetWidthMm + 1e-9
      ? `「${node.title}」的框（${roundMm(lineWidthMm)}mm）太粗：内容放不下（需要 ${roundMm(widthMm + insetMm * 2)}mm，目标是 ${roundMm(targetWidthMm)}mm）—— 把线宽改细一点，或者把宽度改大`
      : null

  /*
   * ★★ **墨色 = 「颜色」参数**（条码 / 二维码上它叫**前景色**：条与模块的颜色）。
   *
   * ⚠️ 元素级（`ElementView.color`）与条码级（`BarcodeView.color`）**必须是
   * 同一个值**，而且要在这里**算一次**：
   *
   *   · `ElementView.color` 是编辑视图那条 `currentColor` 链的源头
   *     （`.el` 的内联 `color`，条 / 模块的 `fill: currentColor` 继承它）；
   *   · `BarcodeView.color` 是 IR 里那条 op 的 `color`（预览与执行侧的墨色）。
   *
   * 原来这两处各写了一份：元素级写死 `'#000000'`，条码级才读参数 ——
   * 于是**改「颜色」参数时编辑视图一动不动**（预览会变，因为它读 IR）。
   * 两个字段的注释本来都写着"它们就是同一件事"，那就让它们真的同源。
   */
  const inkColor = colorOf(evaluated?.color ?? node.values.color, '#000000')

  return {
    id: node.id,
    nodeType: node.type,
    title: node.title,
    kind: 'barcode',
    box,
    text,
    sizeMm: 0,
    align: 'center',
    vAlign: 'top',
    bold: false,
    color: inkColor,
    lineHeightMm: 0,
    opacity: clamp01(Number(evaluated?.opacity ?? numOf(node.values, 'opacity', 1))),
    strokeColor,
    fillColor,
    lineWidthMm,
    fill,
    radiusMm,
    /*
     * ⚠️ **反白（`invert`）仍然不接**，这一条是**已知缺口**、不是"设计如此"：
     * 它不像上面那五件套那样只是"少一条 rect"——
     *   · IR 的 `barcode` op（1d / 2d）**没有** `invert` 字段（`rect` / `text` 才有）；
     *   · 光栅器只会"上墨"，反白要的是"先把整框上墨、再把条 / 模块**擦掉**"
     *     （`raster.ts` 里没有擦除这条路径）；
     *   · 预览得在条码 op 之前铺一条实心黑、再把条 / 模块画成纸色。
     * 也就是它要动**IR 契约 + 三个消费者**，属于另一件事。写在这里免得下一个人
     * 以为"外观都接上了、只是反白没用"是随手的疏漏。
     */
    invert: false,
    rotationDeg: normalizeRotation(evaluated?.rotationDeg ?? node.values.rotationDeg),
    image: null,
    barcode: {
      square,
      symbology,
      label: spec?.label ?? symbology,
      text,
      xDimMicron,
      showHri,
      targetWidthMm,
      targetHeightMm,
      moduleDots,
      moduleFromWidth,
      moduleMm,
      /* ⚠️ 框让出来的内缩量（0 = 没有框）：编辑视图与 IR 都按它把内容挪进框里 */
      insetMm,
      frameWarning,
      contentWidthMm: roundMm(contentMm),
      quietZoneMm: roundMm(quietZoneMm),
      barHeightMm,
      hriMm,
      ecLevel,
      /* ⚠️ 和元素级那个 `color` **同一个值**（见上面 `inkColor` 那段） */
      color: inkColor,
      encoded,
      error,
      xDimWarning: xDimWarning(xDimMicron, dpi),
    },
  }
}

/**
 * **图片元素**：几何和文本 / 矩形一样是框，多的是源与二值化参数。
 *
 * ⚠️ 这里**不判断图片能不能取到**（求值是纯的、离线可重放的）：
 * 只看"地址是不是空的"。取图 / 二值化都是输出侧的事，IR 里带的是地址与阈值。
 */
function imageElementOf(doc: PrintDocView, node: EditorNodeView): ElementView {
  const evaluated = evaluatedComponent(doc, node.id)
  const raw = evaluated?.src ?? node.values.src
  const src = typeof raw === 'string' ? raw.trim() : ''
  const fitRaw = evaluated?.fit ?? node.values.fit
  const fit: ImageView['fit'] = fitRaw === 'cover' || fitRaw === 'fill' ? fitRaw : 'contain'
  return {
    id: node.id,
    nodeType: node.type,
    title: node.title,
    kind: 'image',
    box: boxOf(node),
    text: '',
    sizeMm: 0,
    align: 'left',
    vAlign: 'top',
    bold: false,
    color: '#000000',
    lineHeightMm: 0,
    opacity: clamp01(Number(evaluated?.opacity ?? numOf(node.values, 'opacity', 1))),
    strokeColor: colorOf(evaluated?.strokeColor ?? node.values.strokeColor, '#000000'),
    fillColor: colorOf(evaluated?.fillColor ?? node.values.fillColor, '#ffffff'),
    lineWidthMm: Math.max(0, Number(evaluated?.lineWidthMm ?? numOf(node.values, 'lineWidthMm', 0))),
    fill: Boolean(evaluated?.fill ?? node.values.fill ?? false),
    radiusMm: Math.max(0, Number(evaluated?.radiusMm ?? numOf(node.values, 'radiusMm', 0))),
    invert: false,
    rotationDeg: normalizeRotation(evaluated?.rotationDeg ?? node.values.rotationDeg),
    barcode: null,
    image: {
      src,
      fit,
      threshold: Math.round(clamp(Number(evaluated?.threshold ?? numOf(node.values, 'threshold', 128)), 0, 255)),
      hasSource: src !== '',
    },
  }
}

function elementOf(doc: PrintDocView, node: EditorNodeView, dpi: number): ElementView {
  if (node.type === BARCODE_TYPE || node.type === QRCODE_TYPE) return barcodeElementOf(doc, node, dpi)
  if (node.type === IMAGE_TYPE) return imageElementOf(doc, node)
  const kind: 'text' | 'box' = node.type === BOX_TYPE ? 'box' : 'text'
  const evaluated = evaluatedComponent(doc, node.id)
  const alignRaw = evaluated?.align ?? node.values.align
  const align: ElementView['align'] =
    alignRaw === 'center' || alignRaw === 'right' ? alignRaw : 'left'
  const vAlignRaw = evaluated?.vAlign ?? node.values.vAlign
  const vAlign: TextVAlign =
    vAlignRaw === 'middle' || vAlignRaw === 'bottom' ? vAlignRaw : 'top'
  return {
    id: node.id,
    nodeType: node.type,
    title: node.title,
    kind,
    box: boxOf(node),
    /*
     * ⚠️ 内容优先取**求值结果**（数据绑定之后那才是真值），
     * 没有时回退内联字面量 —— 求值失败不该让编辑器显示空白。
     */
    text: String(evaluated?.text ?? textOf(node.values, 'text', '')),
    sizeMm: Number(evaluated?.sizeMm ?? numOf(node.values, 'sizeMm', 3)),
    align,
    vAlign,
    bold: Boolean(evaluated?.bold ?? node.values.bold ?? false),
    color: colorOf(evaluated?.color ?? node.values.color, '#000000'),
    opacity: clamp01(Number(evaluated?.opacity ?? numOf(node.values, 'opacity', 1))),
    /*
     * 行距 0 = 自动。这里**不**把"自动"算成一个具体 mm：
     * 自动值依赖字号，而字号已经被算进 `sizeMm` —— 两处都算会在
     * "改了字号忘了改行距"时对不上。渲染侧用 `lineHeightOf()`。
     */
    lineHeightMm: Math.max(0, Number(evaluated?.lineHeightMm ?? numOf(node.values, 'lineHeightMm', 0))),
    strokeColor: colorOf(evaluated?.strokeColor ?? node.values.strokeColor, '#000000'),
    fillColor: colorOf(evaluated?.fillColor ?? node.values.fillColor, '#ffffff'),
    lineWidthMm: Number(evaluated?.lineWidthMm ?? numOf(node.values, 'lineWidthMm', 0.3)),
    fill: Boolean(evaluated?.fill ?? node.values.fill ?? false),
    radiusMm: Math.max(0, Number(evaluated?.radiusMm ?? numOf(node.values, 'radiusMm', 0))),
    invert: Boolean(evaluated?.invert ?? node.values.invert ?? false),
    rotationDeg: normalizeRotation(evaluated?.rotationDeg ?? node.values.rotationDeg),
    barcode: null,
    image: null,
  }
}

/** 求值结果里的**已编码符号**（没有 / 形状不对 → undefined） */
function evaluatedEncodeOf(doc: PrintDocView, nodeId: string): EncodedSymbol | undefined {
  const value = doc.outputs.get(nodeId)?.get('out')
  if (!value || typeof value !== 'object') return undefined
  const encoded = (value as { encoded?: unknown }).encoded
  if (!encoded || typeof encoded !== 'object') return undefined
  const kind = (encoded as { kind?: unknown }).kind
  return kind === '1d' || kind === '2d' ? (encoded as EncodedSymbol) : undefined
}

/* ══════════════════════════════════════════════════════════════════
   成员关系（读边）
   ══════════════════════════════════════════════════════════════════ */

/**
 * `items.3` → `3`；不是这一行的句柄 → `null`。
 *
 * ⚠️ 句柄格式是**节点层**定的（`node/rows.ts` 的 `patternOf`：
 * `${row.id}.{i}`）。这里硬写 `items.` 是因为行 id 是我们自己声明的 ——
 * 但**只在求值/校验都走同一条路的前提下**才安全。所以换行 id 时
 * 这一处和 `nodes.ts` 必须一起改（`verify:print` 有一条断言钉着它们一致）。
 */
export function itemsIndexOf(handle: string | null | undefined): number | null {
  return handleIndexOf(handle, 'items')
}

/**
 * `pages.3` → `3`；不是这一行的句柄 → `null`。
 *
 * ⚠️ 行 id 是**参数**（不是写死的 `items.`）：文档的页行叫 `pages`、
 * 页面的组件行叫 `items` —— 同一个规则（`${rowId}.{i}`，见
 * `node/rows.ts` 的 `patternOf`）当然要一个实现。第一版把 `items.`
 * 写死在 `itemsIndexOf` 里，于是页序解析**永远是 null**（所有页并列），
 * 而这种错的表现只是"页序不对"，很容易被当成排序问题去查。
 */
export function handleIndexOf(handle: string | null | undefined, rowId: string): number | null {
  const prefix = `${rowId}.`
  if (!handle || !handle.startsWith(prefix)) return null
  const index = Number(handle.slice(prefix.length))
  return Number.isInteger(index) && index >= 0 ? index : null
}

/**
 * **行距**（mm）：显式给了就用它，0 = 自动（字号的 1.15 倍）。
 *
 * ⚠️ 只许有这一处实现：行距同时决定"编辑器里几行排得下"、"IR 里 text op
 * 的行距字段"、"预览里那几行字的疏密"。三处各算一次的表现是
 * **编辑器里 3 行、打印出来挤成 2 行半** —— 而这种错只有出纸才看得见。
 */
export function lineHeightOf(element: Pick<ElementView, 'lineHeightMm' | 'sizeMm'>): number {
  return element.lineHeightMm > 0 ? element.lineHeightMm : element.sizeMm * 1.15
}

/**
 * 某个页面上接了哪些组件（按 `items.N` 的序号排序）。
 *
 * ⚠️ `dpi` 从**页面**来（不是组件的参数）：条码要把模块宽吸附成整数点，
 * 而"点"是什么尺寸由页面分辨率决定。默认 203 只是给调用方省事
 * （测试 / 单组件场景），编辑器与布局一律显式传。
 */
export function elementsOf(doc: PrintDocView, pageId: string, dpi = DEFAULT_DPI): ElementView[] {
  const byId = new Map(doc.nodes.map((node) => [node.id, node]))
  const wired: { index: number; node: EditorNodeView }[] = []
  for (const link of doc.links) {
    if (link.target !== pageId) continue
    const index = itemsIndexOf(link.targetHandle)
    if (index === null) continue
    const node = byId.get(link.source)
    if (node && isComponentType(node.type)) wired.push({ index, node })
  }
  wired.sort((a, b) => a.index - b.index)
  return wired.map((entry) => elementOf(doc, entry.node, dpi))
}

/**
 * 下一个空槽的句柄（`items.0` / `items.1` …）。
 *
 * ⚠️ 找的是**第一个空洞**而不是「已连个数」：变参行要求压紧
 * （见 README「变参行必须压紧」），但用户手动断开中间一路之后
 * 「已连个数」和「最后一个序号」就不一致了 —— 按个数算会**覆盖**已有连线
 * （同一个输入句柄两条边），而那在界面上表现为"接上去没反应"。
 */
export function nextItemsHandle(links: readonly EditorLinkView[], pageId: string): string {
  const used = new Set<number>()
  for (const link of links) {
    if (link.target !== pageId) continue
    const index = itemsIndexOf(link.targetHandle)
    if (index !== null) used.add(index)
  }
  let index = 0
  while (used.has(index)) index += 1
  return `items.${index}`
}

/**
 * **下一个空槽的页码句柄**（`pages.0` / `pages.1` …）—— 和 `nextItemsHandle` 同一个道理。
 *
 * ⚠️ 别用「已有几页」当序号（`pages.${pages.length}` 那种写法）：页码**不压实**
 * （见 `orderedPages`），断开中间那一路之后"页数"和"最后一个槽位号"就不一致了 ——
 * 按页数算会把新页接到**已经被占**的槽位上，而宿主的覆盖语义（一个输入一条边）
 * 会把原来那一页**顶掉**。表现是"加了一页，另一页没了"。
 */
export function nextPagesHandle(links: readonly EditorLinkView[], documentId: string): string {
  const used = new Set<number>()
  for (const link of links) {
    if (link.target !== documentId) continue
    const index = handleIndexOf(link.targetHandle, 'pages')
    if (index !== null) used.add(index)
  }
  let index = 0
  while (used.has(index)) index += 1
  return `pages.${index}`
}

/** 这个组件接在哪个页面上（没接 → null）。用于「未接入」清单与"加组件加到哪一页" */
export function pageIdOfComponent(doc: PrintDocView, nodeId: string): string | null {
  const byId = new Map(doc.nodes.map((node) => [node.id, node]))
  for (const link of doc.links) {
    if (link.source !== nodeId || itemsIndexOf(link.targetHandle) === null) continue
    const target = byId.get(link.target)
    if (target && isPageType(target.type)) return target.id
  }
  return null
}

/** 文档里的组件节点（不看有没有接页面） */
export function componentNodes(doc: PrintDocView): EditorNodeView[] {
  return doc.nodes.filter((node) => isComponentType(node.type))
}

/**
 * **没接到任何页面上的组件**。
 *
 * ⚠️ 这个清单是有用的，不是调试信息：节点图里删掉一条边之后，
 * 组件就"消失"了（页面编辑器不画它），而用户会以为是编辑器丢了东西。
 * 面板把它列出来 + 一个「接到本页」，缺的那一步就补上了。
 */
export function orphanComponents(doc: PrintDocView): EditorNodeView[] {
  const wired = new Set<string>()
  for (const link of doc.links) {
    if (itemsIndexOf(link.targetHandle) === null) continue
    const target = doc.nodes.find((node) => node.id === link.target)
    if (target && isPageType(target.type)) wired.add(link.source)
  }
  return componentNodes(doc).filter((node) => !wired.has(node.id))
}

/** 文档里的页面（按文档顺序 —— 多页时顺序就是文档顺序） */
export function pagesOf(doc: PrintDocView): PageView[] {
  const paper = resolvedPaperOf(doc)
  return orderedPages(doc).map(({ node, index }) => ({
    id: node.id,
    /* ⚠️ 实例标识 = 节点 + **槽位**：同一页印两次时两张纸必须分得开（见 `PageView.key`） */
    key: pageKeyOf(node.id, index),
    index,
    title: node.title,
    ...paper,
    elements: elementsOf(doc, node.id, paper.dpi),
  }))
}

/**
 * 一张纸的实例标识（`${节点id}#${槽位}`）。
 *
 * ⚠️ 槽位而不是数组下标：空槽跳过之后，数组下标和 `pages.N` 不是一回事 ——
 * 用下标当标识的话，"刷新一下 key 就变了"（Vue 会重建 DOM，指针拖动跟着断）。
 */
export function pageKeyOf(nodeId: string, index: number): string {
  return `${nodeId}#${index}`
}

/**
 * **纸张从哪来**（三级，顺序是刻意的）：
 *
 * ```text
 *   ① 文档节点的参数     ← 正常路径（真机反馈：纸张属于文档）
 *   ② 页面自己的参数     ← **旧文档的迁移路径**（纸张曾经在页面节点上）
 *   ③ DEFAULT_PAPER      ← 两个都没有时
 * ```
 *
 * ⚠️ ② 只是**读**得到，不是界面上的控件：参数表里 `widthMm` 这些已经归
 * 文档了，所以新文档不会在页面上产生这些值。留着这条读取路径是为了
 * 不把旧模板读成 60×30（那会让老模板**静默变形**）。
 *
 * ⚠️ 一份文档里**所有页共用纸张**：十页各自一套 dpi 是没人想要的东西
 * （每一页的物理尺寸本来就该一样），而且输出侧按"一份活一个介质"处理。
 */
export function resolvedPaperOf(doc: PrintDocView): {
  widthMm: number
  heightMm: number
  dpi: number
  marginMm: number
  backgroundColor: string
} {
  const documentNode = doc.nodes.find((node) => node.type === DOCUMENT_TYPE)
  const source = documentNode ?? doc.nodes.find((node) => isPageType(node.type))
  if (!source) return { ...DEFAULT_PAPER, backgroundColor: DEFAULT_BACKGROUND }
  return {
    widthMm: numOf(source.values, 'widthMm', DEFAULT_PAPER.widthMm),
    heightMm: numOf(source.values, 'heightMm', DEFAULT_PAPER.heightMm),
    dpi: numOf(source.values, 'dpi', DEFAULT_PAPER.dpi),
    marginMm: numOf(source.values, 'marginMm', DEFAULT_PAPER.marginMm),
    /* ⚠️ 颜色走 `colorOf`：只认 `#rgb` / `#rrggbb`（值会进 IR 交给输出侧，
       那边不该带一个 CSS 解析器） */
    backgroundColor: colorOf(source.values.backgroundColor, DEFAULT_BACKGROUND),
  }
}

/** 文档节点（没有就是"单页模式"：纸张读页面上的旧值） */
export function documentNodeOf(doc: PrintDocView): EditorNodeView | null {
  return doc.nodes.find((node) => node.type === DOCUMENT_TYPE) ?? null
}

/**
 * **升级为文档时该抄哪份纸张**。
 *
 * ⚠️ 抽成纯函数是为了能被断言：不抄的话，一份 60×40 的单页模板升级之后
 * 会当场变成 60×30（页面上的旧值只是**只读兜底**，新文档用它自己的默认值）。
 * 这种事在界面上表现为"点了一下按钮，版面全变了"，而它只发生在
 * 升级那一刻 —— 没有断言就只能靠人碰巧发现。
 */
export function documentSeedFrom(pages: readonly PageView[]): {
  widthMm: number
  heightMm: number
  dpi: number
  marginMm: number
  backgroundColor: string
} {
  const first = pages[0]
  if (!first) return { ...DEFAULT_PAPER, backgroundColor: DEFAULT_BACKGROUND }
  return {
    widthMm: first.widthMm,
    heightMm: first.heightMm,
    dpi: first.dpi,
    marginMm: first.marginMm,
    backgroundColor: first.backgroundColor,
  }
}

/**
 * **编辑器里的页** —— 有文档节点时：文档 `pages.N` 上真的接着的来源，按槽位排序。
 *
 * ## ★★ 语义（真机反馈逼出来的两条）
 *
 * 真机反馈原话：「文档的页面输入，我把同一个页面链接到文档两次，没有产生两个
 * 页面，并且我断开新建的页面链接到文档，编辑器还是有那个新建页面」。
 * 两条根因都在这一个函数里：
 *
 * ```text
 *   ① 遍历的是**图上所有页面节点** → 没接到文档的也算一页
 *      （序号给 MAX_SAFE_INTEGER 兜底，于是它永远排在最后、还占着一张纸）
 *   ② 按**节点**去重（每个节点 find 第一条边）→ 同一个节点接两个槽位只算一页
 * ```
 *
 * 现在的规矩（一条边一页）：
 *
 * | 情形 | 结果 |
 * |---|---|
 * | 同一个页面节点接 `pages.0` 和 `pages.1` | **两页**（物理上就是同一页印两次） |
 * | 页面节点没接到任何 `pages.N` | **不出现**（它在图上只是个节点） |
 * | 中间槽位空着（`.0` 接着、`.1` 空着、`.2` 接着） | 空槽跳过，但**序号仍按槽位**（第 3 页显示"3"） |
 * | 没有文档节点（单页模式） | 图上所有页面节点（老行为，别改） |
 *
 * ⚠️ **顺序读句柄，不读边的数组顺序**：后者在删一条边再加回来之后会变，
 * 表现是"第 2 页莫名其妙跑到第 1 页"。这一条和 `items`（组件的层序）是同一条规矩。
 *
 * ⚠️ **空槽不压实**（和组件那条不一样，别照抄）：层序是"谁压着谁"，压紧没有信息损失；
 * 而页码是用户在连线上摆出来的东西 —— 中间空一格时把它压实，用户看到的页码
 * 就和他接的那一路对不上了。
 */
export function orderedPages(doc: PrintDocView): { node: EditorNodeView; index: number }[] {
  const pages = doc.nodes.filter((node) => isPageType(node.type))
  const documentNode = documentNodeOf(doc)
  if (!documentNode) return pages.map((node, index) => ({ node, index }))
  const byId = new Map(pages.map((node) => [node.id, node]))
  const wired: { node: EditorNodeView; index: number }[] = []
  for (const link of doc.links) {
    if (link.target !== documentNode.id) continue
    const index = handleIndexOf(link.targetHandle, 'pages')
    if (index === null) continue
    const node = byId.get(link.source)
    /* ⚠️ 接在 `pages.N` 上的东西不是页面节点（类型校验已经拦了一道）→ 跳过，
       不能让它变成一张"没有页面的纸" */
    if (node) wired.push({ node, index })
  }
  return wired.sort((a, b) => a.index - b.index)
}

/**
 * **「当前是这一页」** —— 选中的组件在哪个页面上，就是哪一页；没选中就是第一页。
 *
 * ⚠️ 抽成纯函数是因为**两个地方**都要这个答案：编辑器（新组件加到哪一页）
 * 和面板（显示哪一页的设置）。两处各写一遍的表现是
 * 「面板显示第 2 页、加组件却加到第 1 页」—— 那是最难解释的一类 bug。
 */
export function activePageOf(
  pages: readonly PageView[],
  selection: readonly string[],
): PageView | null {
  if (pages.length === 0) return null
  for (const id of selection) {
    const owner = pages.find((page) => page.elements.some((el) => el.id === id))
    if (owner) return owner
  }
  return pages[0]!
}

/* ══════════════════════════════════════════════════════════════════
   几何：命中 / 移动 / 缩放 / 夹取
   ══════════════════════════════════════════════════════════════════ */

/** 组件的最小尺寸（mm）—— 比这更小的框在 203dpi 上只有 8 个点，拖不动也看不见 */
export const MIN_ELEMENT_MM = 1

/**
 * 命中判定：点在哪一个元素上（没命中 → null）。
 *
 * ⚠️ **从后往前**扫：数组顺序就是层序，后面的画在上面 ——
 * 命中必须和眼睛看到的一致，否则"点上面那个却选中下面那个"。
 */
/**
 * **把世界坐标里的位移，换成组件自己坐标系里的位移。**
 *
 * ⚠️ 旋转之后这两者不一样：组件转了 90°，屏幕上"往右拖"在组件的局部
 * 就是"往下拖"。不做这一次换算的话，拖旋转后的组件会**沿着屏幕轴走**
 * —— 而用户看着那个框，期待的是它沿着自己的边长大。
 *
 * 只支持 90° 的整数倍，所以这里就是**换轴 + 换号**，不涉及三角函数：
 *
 * | 角度 | 局部 x | 局部 y |
 * |---|---|---|
 * | 0° | dx | dy |
 * | 90°（顺时针） | dy | −dx |
 * | 180° | −dx | −dy |
 * | 270° | −dy | dx |
 */
export function toLocalDelta(dxMm: number, dyMm: number, rotationDeg: number): { dxMm: number; dyMm: number } {
  switch (normalizeRotation(rotationDeg)) {
    case 90:
      return { dxMm: dyMm, dyMm: -dxMm }
    case 180:
      return { dxMm: -dxMm, dyMm: -dyMm }
    case 270:
      return { dxMm: -dyMm, dyMm: dxMm }
    default:
      return { dxMm, dyMm }
  }
}

/**
 * **把世界坐标的点，转进组件自己的坐标系**（`hitTest` 用）。
 *
 * 绕**框中心**转 `-rotationDeg`（顺时针的逆运算），和 CSS 里那句
 * `rotate` 的枢轴一致 —— 两边枢轴不一样的话，画在哪儿和点得中哪儿会错开
 * 半个框（旋转 90° 时最明显）。
 */
export function toLocalPoint(
  xMm: number,
  yMm: number,
  box: Pick<ElementBox, 'xMm' | 'yMm' | 'wMm' | 'hMm'>,
  rotationDeg: number,
): { xMm: number; yMm: number } {
  const angle = normalizeRotation(rotationDeg)
  if (angle === 0) return { xMm, yMm }
  const cx = box.xMm + box.wMm / 2
  const cy = box.yMm + box.hMm / 2
  const dx = xMm - cx
  const dy = yMm - cy
  /* 逆时针转回来：90° 的逆是 (dy, −dx) —— 和 `toLocalDelta` 是同一条规矩 */
  const local = toLocalDelta(dx, dy, 360 - angle)
  return { xMm: cx + local.dxMm, yMm: cy + local.dyMm }
}

export function hitTest(elements: readonly ElementView[], xMm: number, yMm: number): ElementView | null {
  /*
   * ⚠️ **从后往前**：数组顺序就是层序，后面的画在上面 ——
   * 命中判定必须和眼睛看到的一致，否则"点上面那个却选中下面那个"。
   */
  for (let i = elements.length - 1; i >= 0; i -= 1) {
    const el = elements[i]!
    /* 先把点转进这个组件自己的坐标系，再判轴对齐的框（见 `toLocalPoint`） */
    const local = toLocalPoint(xMm, yMm, el.box, el.rotationDeg)
    if (
      local.xMm >= el.box.xMm &&
      local.xMm <= el.box.xMm + el.box.wMm &&
      local.yMm >= el.box.yMm &&
      local.yMm <= el.box.yMm + el.box.hMm
    ) {
      return el
    }
  }
  return null
}

/** 把框夹进纸内（拖出去的元素在预览里"消失"，那看起来像 bug） */
/**
 * 拖动时的兜底夹取。
 *
 * ⚠️ **允许负数、也允许伸出纸外**（真机反馈："组件需要支持负数，
 * 而不是只能在 0 的位置"）—— 贴边 / 出血的组件本来就该能摆到纸外边界上，
 * 越界的部分由 IR 的检查去提醒（那是**看得见**的提示，而不是拖不动）。
 *
 * ⚠️ 唯一的硬约束是**别把组件拖到完全够不着**：至少留
 * `MIN_VISIBLE_MM` 在纸内，否则它既看不见也点不中（只能靠删节点找回）。
 */
export const MIN_VISIBLE_MM = 3

export function clampBoxToPage(box: ElementBox, page: { widthMm: number; heightMm: number }): ElementBox {
  const wMm = clamp(box.wMm, MIN_ELEMENT_MM, Math.max(MIN_ELEMENT_MM, page.widthMm))
  const hMm = clamp(box.hMm, MIN_ELEMENT_MM, Math.max(MIN_ELEMENT_MM, page.heightMm))
  const visibleX = Math.min(MIN_VISIBLE_MM, wMm)
  const visibleY = Math.min(MIN_VISIBLE_MM, hMm)
  /* ⚠️ 键序按 `ElementBox` 的声明（x / y / w / h）—— 断言比的是 JSON，
     顺手让打印出来的对象和类型定义读起来一致 */
  return {
    xMm: roundMm(clamp(box.xMm, -(wMm - visibleX), Math.max(0, page.widthMm - visibleX))),
    yMm: roundMm(clamp(box.yMm, -(hMm - visibleY), Math.max(0, page.heightMm - visibleY))),
    wMm: roundMm(wMm),
    hMm: roundMm(hMm),
  }
}

export type ResizeHandle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

export const RESIZE_HANDLES: readonly ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']

/** 按 8 向把手缩放（dx / dy 是 mm 位移，从**起手时那份框**算起，不是逐帧累加） */
export function resizeBox(
  box: ElementBox,
  handle: ResizeHandle,
  dxMm: number,
  dyMm: number,
  page: { widthMm: number; heightMm: number },
): ElementBox {
  const west = handle.includes('w')
  const east = handle.includes('e')
  const north = handle.includes('n')
  const south = handle.includes('s')

  let left = box.xMm
  let top = box.yMm
  let right = box.xMm + box.wMm
  let bottom = box.yMm + box.hMm

  /* 夹在纸内：先定边，再保证最小尺寸（顺序反了会出现"翻面"的负宽） */
  if (west) left = clamp(left + dxMm, 0, right - MIN_ELEMENT_MM)
  if (east) right = clamp(right + dxMm, left + MIN_ELEMENT_MM, page.widthMm)
  if (north) top = clamp(top + dyMm, 0, bottom - MIN_ELEMENT_MM)
  if (south) bottom = clamp(bottom + dyMm, top + MIN_ELEMENT_MM, page.heightMm)

  return {
    xMm: roundMm(left),
    yMm: roundMm(top),
    wMm: roundMm(right - left),
    hMm: roundMm(bottom - top),
  }
}

/* ══════════════════════════════════════════════════════════════════
   条码的缩放：拖的是**参数**，不是框
   ══════════════════════════════════════════════════════════════════ */

/**
 * 条码被拖动 / 缩放之后，该写回哪些**参数**。
 *
 * ⚠️ 条码没有"宽度"参数（宽度 = 模块数 × 整数点模块宽），所以左右拉动
 * 改的是 **X 维度（µm）**：目标宽度 → 每模块几个整数点 → 反推微米。
 * 这是唯一能让"拖宽一点"和"条宽必须是整数点"同时成立的写法。
 * 上下拉动改的是**条高**。写回的参数由这里算，编辑器只负责落笔。
 *
 * ⚠️ 反推时**先算点数再算微米**（不是先算微米再取整）：
 * 顺序反了会得到"面板显示 330µm、实际 3 点"这种看着一致、
 * 但用户再拖一下数值会跳的错误手感。
 */
/**
 * 拖动条码时会被改的那几个参数。
 *
 * ⚠️ **宽高在里面，X 维度不在**：宽度是用户直接拖的东西，
 * 模块宽是布局根据宽度算出来的（见 `barcodeElementOf` 里那段说明）。
 */
export interface BarcodeParams {
  xMm: number
  yMm: number
  widthMm: number
  heightMm: number
}

export function resizeBarcode(
  params: BarcodeParams,
  handle: ResizeHandle,
  dxMm: number,
  dyMm: number,
  view: { moduleDots: number; moduleMm: number; modules: number; square: boolean },
  page: { widthMm: number; heightMm: number },
  dpi: number,
): BarcodeParams {
  void view
  void dpi
  const west = handle.includes('w')
  const east = handle.includes('e')
  const north = handle.includes('n')
  const south = handle.includes('s')

  let { xMm, yMm, widthMm, heightMm } = params

  /*
   * ⚠️ **拖的是宽高本身**（不再反推 X 维度）。
   *
   * 用户在真机上说的很直接：「旋转之后拖动用的是旋转后的坐标系，很反人类」
   * +「换符号就画出框外」。两条都指向同一件事：**框是框，图形是框里算出来的**。
   * 所以拖动只改 `wMm` / `hMm`（文档里的目标尺寸），模块宽留给布局去取整 ——
   * 顺带也让拖动不再有"最小一跳是一个模块"的颗粒感（那是以前 X 维度
   * 反推带来的手感问题）。
   */
  if (west) {
    const next = Math.max(MIN_ELEMENT_MM, widthMm - dxMm)
    xMm = xMm + (widthMm - next)
    widthMm = next
  } else if (east) {
    widthMm = Math.max(MIN_ELEMENT_MM, widthMm + dxMm)
  }
  if (north) {
    const next = Math.max(MIN_ELEMENT_MM, heightMm - dyMm)
    yMm = yMm + (heightMm - next)
    heightMm = next
  } else if (south) {
    heightMm = Math.max(MIN_ELEMENT_MM, heightMm + dyMm)
  }
  if (view.square) {
    /* 二维码：拖任意一边都是改边长（高跟着宽） */
    const side = west || east ? widthMm : heightMm
    widthMm = side
    heightMm = side
  }
  return {
    xMm: roundMm(xMm),
    yMm: roundMm(yMm),
    widthMm: roundMm(widthMm),
    heightMm: roundMm(heightMm),
  }
}


/** 条码最小条高（mm）—— 低于它一维码几乎扫不出来（规范建议 ≥5mm） */
export const MIN_BARCODE_HEIGHT_MM = 5

/* ══════════════════════════════════════════════════════════════════
   吸附：网格 + 元素/纸面参考线
   ══════════════════════════════════════════════════════════════════ */

export interface SnapGuide {
  /** 参考线的位置（mm） */
  atMm: number
  axis: 'x' | 'y'
}

export interface SnapTargets {
  xs: readonly number[]
  ys: readonly number[]
}

export interface SnapOptions {
  /** 网格步长 mm（0 = 不吸网格） */
  gridMm: number
  /** 吸附阈值 mm（由屏幕像素换算过来 —— 手感是像素级的，不是毫米级的） */
  thresholdMm: number
}

/**
 * 参考线候选：纸的两边 + 纸的中线 + 其它元素的边与中线。
 *
 * ⚠️ **不包含自己**（`exceptId`）：包含自己的话，拖动时它每次都吸回原位，
 * 表现是"拖不动"。
 */
export function snapTargetsFor(
  page: { widthMm: number; heightMm: number },
  elements: readonly ElementView[],
  exceptId?: string,
  /**
   * **正在拖的那个框**（给了它才会有"等距"候选）。
   *
   * ⚠️ 等距候选**依赖被拖框的尺寸**（要算"放在这里两边空隙一样大吗"），
   * 所以它没法像普通参考线那样预先算好一张固定表 ——
   * 这也是它当初没做的原因（当时 `snapTargetsFor` 只接收页面与元素）。
   */
  movingBox?: { wMm: number; hMm: number },
): SnapTargets {
  const xs = [0, page.widthMm / 2, page.widthMm]
  const ys = [0, page.heightMm / 2, page.heightMm]
  for (const el of elements) {
    if (el.id === exceptId) continue
    xs.push(el.box.xMm, el.box.xMm + el.box.wMm / 2, el.box.xMm + el.box.wMm)
    ys.push(el.box.yMm, el.box.yMm + el.box.hMm / 2, el.box.yMm + el.box.hMm)
  }
  if (movingBox) {
    const others = elements.filter((el) => el.id !== exceptId)
    xs.push(
      ...equalGapCandidates(
        others.map((el) => ({ start: el.box.xMm, end: el.box.xMm + el.box.wMm })),
        movingBox.wMm,
      ),
    )
    ys.push(
      ...equalGapCandidates(
        others.map((el) => ({ start: el.box.yMm, end: el.box.yMm + el.box.hMm })),
        movingBox.hMm,
      ),
    )
  }
  return { xs, ys }
}

/**
 * **等距**候选：让"空隙和别处一样大"的那些位置。
 *
 * 两种，都是用户真的会想要的那两种：
 *
 *   ① **居中于两者之间**：左邻右舍各留一样大的空隙
 *      （A —— 新 —— B，两段空隙相等）；
 *   ② **延续已有的节奏**：已经有两个组件之间是 8mm 的缝，那么新组件接在
 *      右边/左边时也留 8mm（A —8— B —8— 新）。
 *
 * ⚠️ 这里把"一维投影"当近似：**不分行也不分列**（A 在第 1 行、B 在第 5 行，
 * 它们的中缝照样算候选）。分带要引入行的概念，而模板上的组件本来就不多 ——
 * 多几个候选的代价是"偶尔吸到一个远处的节奏上"，比"干脆不吸"更接近用户预期。
 *
 * ⚠️ 候选只返回**被拖框的左边缘**该落在哪儿（`start`），和别的参考线一个口径。
 */
function equalGapCandidates(
  intervals: readonly { start: number; end: number }[],
  size: number,
): number[] {
  const out: number[] = []
  /* ① 居中于两者之间 */
  for (const a of intervals) {
    for (const b of intervals) {
      if (b.start < a.end) continue
      const space = b.start - a.end
      if (space <= size) continue
      out.push(a.end + (space - size) / 2)
    }
  }
  /* ② 延续节奏：g = 已有的一段缝 */
  for (const a of intervals) {
    for (const b of intervals) {
      if (b === a || b.start < a.end) continue
      const gap = b.start - a.end
      if (gap <= 0) continue
      out.push(b.end + gap)
      out.push(a.start - gap - size)
    }
  }
  return out
}

/** 一个轴上的吸附结果 */
interface AxisSnap {
  delta: number
  guide: number | null
}

/**
 * 在**一个轴**上找最近的吸附。
 *
 * 候选位置（`values`）是"这个框在这个轴上的三个代表"：
 * 左 / 中 / 右（或上 / 中 / 下）。
 *
 * ## ★ 参考线**优先于**网格（这是真机反馈改出来的）
 *
 * 原来的规则是"两者都在阈值内时选位移更小的那个"—— 听起来公平，实际表现是
 * **网格几乎总是赢**：网格是每 1mm 一根的密集候选，而别的组件的边缘往往
 * 差着零点几毫米。于是用户拖着组件去找另一条边，它却老是吸到网格上，
 * 感受就是"只有网格吸附，没有组件之间的边缘 / 居中吸附"。
 *
 * 现在：**只要参考线在阈值内就用它**（多次命中取最近的一条），
 * 网格只在"没有任何参考线在附近"时兜底。对齐到别的组件是明确的意图，
 * 网格只是"别放歪"的底线。
 */
function snapAxis(
  values: readonly number[],
  targets: readonly number[],
  options: SnapOptions,
): AxisSnap {
  let best: AxisSnap = { delta: 0, guide: null }
  let bestDistance = Number.POSITIVE_INFINITY

  /* ① 参考线：取阈值内最近的一条 */
  for (const value of values) {
    for (const target of targets) {
      const delta = target - value
      const distance = Math.abs(delta)
      if (distance > options.thresholdMm) continue
      if (distance < bestDistance) {
        best = { delta, guide: target }
        bestDistance = distance
      }
    }
  }
  if (best.guide !== null) return best

  /* ② 兜底：网格 */
  if (options.gridMm > 0) {
    for (const value of values) {
      const snapped = Math.round(value / options.gridMm) * options.gridMm
      const delta = snapped - value
      const distance = Math.abs(delta)
      if (distance > options.thresholdMm) continue
      if (distance < bestDistance) {
        best = { delta, guide: null }
        bestDistance = distance
      }
    }
  }
  return best
}

export interface SnapResult {
  box: ElementBox
  guides: SnapGuide[]
}

/**
 * 移动时的吸附：整框位移，取 x / y 各自最近的吸附。
 *
 * ⚠️ 返回的 `guides` 是**画参考线用的**（用户在拖动时要看见"对齐到谁了"）——
 * 光吸不画线，手感是"卡顿"而不是"对齐"。
 */
export function snapBox(
  box: ElementBox,
  targets: SnapTargets,
  options: SnapOptions,
): SnapResult {
  const x = snapAxis([box.xMm, box.xMm + box.wMm / 2, box.xMm + box.wMm], targets.xs, options)
  const y = snapAxis([box.yMm, box.yMm + box.hMm / 2, box.yMm + box.hMm], targets.ys, options)
  const guides: SnapGuide[] = []
  if (x.guide !== null) guides.push({ atMm: x.guide, axis: 'x' })
  if (y.guide !== null) guides.push({ atMm: y.guide, axis: 'y' })
  return {
    box: {
      ...box,
      xMm: roundMm(box.xMm + x.delta),
      yMm: roundMm(box.yMm + y.delta),
    },
    guides,
  }
}

/**
 * 缩放时的吸附：只吸**正在动的那条边**。
 *
 * ⚠️ 不能复用 `snapBox`（它对三条候选线一起吸）：拖右下角时把左边也吸过去，
 * 表现是"缩放时整个框在跳"。
 */
export function snapResize(
  box: ElementBox,
  handle: ResizeHandle,
  targets: SnapTargets,
  options: SnapOptions,
): SnapResult {
  let next = box
  const guides: SnapGuide[] = []
  const west = handle.includes('w')
  const east = handle.includes('e')
  const north = handle.includes('n')
  const south = handle.includes('s')

  if (west || east) {
    const edge = west ? box.xMm : box.xMm + box.wMm
    const snap = snapAxis([edge], targets.xs, options)
    if (snap.delta !== 0) {
      next = west
        ? { ...next, xMm: roundMm(next.xMm + snap.delta), wMm: roundMm(next.wMm - snap.delta) }
        : { ...next, wMm: roundMm(next.wMm + snap.delta) }
    }
    if (snap.guide !== null) guides.push({ atMm: snap.guide, axis: 'x' })
  }
  if (north || south) {
    const edge = north ? box.yMm : box.yMm + box.hMm
    const snap = snapAxis([edge], targets.ys, options)
    if (snap.delta !== 0) {
      next = north
        ? { ...next, yMm: roundMm(next.yMm + snap.delta), hMm: roundMm(next.hMm - snap.delta) }
        : { ...next, hMm: roundMm(next.hMm + snap.delta) }
    }
    if (snap.guide !== null) guides.push({ atMm: snap.guide, axis: 'y' })
  }
  return { box: next, guides }
}

/* ══════════════════════════════════════════════════════════════════
   屏幕 ↔ 纸面：缩放与标尺
   ══════════════════════════════════════════════════════════════════ */

/**
 * CSS 基准下 1mm 有多少 px。
 *
 * ⚠️ 这是**名义值**：CSS 规定 1in = 96px，于是 1mm = 96/25.4 = 3.7795px。
 * 真实物理尺寸还取决于屏幕 DPI（浏览器拿不到可靠值），所以「1:1」
 * 按钮的语义是"按 CSS 基准的实际尺寸"，不是"量出来一定准确"。
 * 这个取舍写在这儿，免得以后有人拿尺子量了说 bug。
 */
export const BASE_PX_PER_MM = 96 / 25.4

export const MIN_ZOOM = 0.1
export const MAX_ZOOM = 8

export function pxPerMm(zoom: number): number {
  return BASE_PX_PER_MM * zoom
}

export function mmToPx(mm: number, zoom: number): number {
  return mm * pxPerMm(zoom)
}

export function pxToMm(px: number, zoom: number): number {
  return px / pxPerMm(zoom)
}

/** 适应窗口：把整页塞进可视区（留一圈 padding） */
export function fitZoom(
  pageWidthMm: number,
  pageHeightMm: number,
  viewWidthPx: number,
  viewHeightPx: number,
  paddingPx = 28,
): number {
  const usableW = Math.max(1, viewWidthPx - paddingPx * 2)
  const usableH = Math.max(1, viewHeightPx - paddingPx * 2)
  const zoom = Math.min(
    usableW / (pageWidthMm * BASE_PX_PER_MM),
    usableH / (pageHeightMm * BASE_PX_PER_MM),
  )
  return clamp(zoom, MIN_ZOOM, MAX_ZOOM)
}

const RULER_STEPS_MM = [1, 2, 5, 10, 20, 50, 100, 200] as const

/**
 * 标尺刻度步长（mm）—— 挑第一个"屏幕上够宽"的档。
 *
 * ⚠️ 固定步长在缩放之后必然有一头不能用：放大到 4× 时 10mm 一格变成
 * 150px（浪费），缩小到 0.3× 时 10mm 一格只有 11px（挤成一团）。
 * 所以步长是**缩放的函数**，不是常量。
 */
export function rulerStepMm(zoom: number, minLabelPx = 48): number {
  for (const step of RULER_STEPS_MM) {
    if (mmToPx(step, zoom) >= minLabelPx) return step
  }
  return RULER_STEPS_MM[RULER_STEPS_MM.length - 1]!
}

/** 0 到 lengthMm 之间所有刻度（含 0，不含超出末尾的） */
export function rulerTicks(lengthMm: number, stepMm: number): number[] {
  if (!(stepMm > 0)) return [0]
  const out: number[] = []
  for (let value = 0; value <= lengthMm + 1e-9; value += stepMm) out.push(roundMm(value))
  return out
}
